import React from "react";
import { Controller, SubmitHandler, useForm, useWatch } from "react-hook-form";
import Select from "react-select";
import { LuAlertCircle, LuFolder } from "react-icons/lu";
import { toast } from "react-toastify";

import { useAppSelector } from "../../app/hooks";
import { getItemCategoryOptions } from "../../features/items/itemsSlice";
import { GET } from "../../services/axios.service";
import { inheritedSizeRows, sizesPayload } from "../../static/itemSize";
import { RateField, rateNumber } from "../../static/processRate";
import { iconSelectStyles, toastStyles } from "../../static/styles";
import { IFormInput } from "../../types/item";
import { FileTextIcon, TagIcon } from "../icons";
import ItemPhoto from "../itemPhoto";
import Button from "../ui/Button";
import Input from "../ui/Input";
import Textarea from "../ui/Textarea";
import cx from "../ui/cx";
import {
  buttonSpinnerClass,
  cardContentClass,
  fieldErrorIconClass,
  fieldErrorTextClass,
  fieldInputClass,
  fieldLabelClass,
  fieldTextareaClass,
  formCardClass,
  formFieldIconClass,
  formSelectIconClass,
  gradientSubmitButtonClass,
} from "../ui/styles";
import ItemMaterialField from "./ItemMaterialField";
import ItemSizeRates from "./ItemSizeRates";
import SubItemsForm, {
  emptySubItem,
  subItemCodeProblems,
  subItemCodes,
} from "./SubItemsForm";

const checkboxClass =
  "h-4 w-4 flex-shrink-0 cursor-pointer rounded border-gray-300 accent-[#113F67]";

export const emptyItemForm = (): IFormInput => ({
  category_id: null,
  name: "",
  description: "",
  image: null,
  image_url: null,
  has_sub_items: false,
  material_id: null,
  cutting_rate: "",
  turning_rate: "",
  vmc_rate: "",
  cut_length: "",
  sizes: [],
  sub_items: [{ ...emptySubItem }],
});

const noRates = { cutting_rate: null, turning_rate: null, vmc_rate: null };

// The item's or a sub item's own rates. With sizes it has its rates per size, so these are sent empty.
const ownRates = (row: Pick<IFormInput, RateField>, hasSizes: boolean) =>
  hasSizes
    ? noRates
    : {
        cutting_rate: rateNumber(row.cutting_rate),
        turning_rate: rateNumber(row.turning_rate),
        vmc_rate: rateNumber(row.vmc_rate),
      };

// The item's or a sub item's own cut length, kept only without sizes.
const ownCutLength = (row: Pick<IFormInput, "cut_length">, hasSizes: boolean) =>
  hasSizes ? null : rateNumber(row.cut_length);

// Request body for create and update. A blank rate or length is sent as null. An item built from sub items never goes
// to cutting, turning or VMC (its sub items do), so it has no material, rates or cut length of its own: it only picks
// the sizes, and every sub item has those same sizes with its own cut lengths and rates. A sub item is sent with its
// code only when one was typed; the server makes the automatic ones.
export const itemPayload = (data: IFormInput) => {
  const hasSubItems = data.has_sub_items;
  const hasSizes = data.sizes.length > 0;

  return {
    category_id: data.category_id,
    name: data.name,
    description: data.description,
    image: data.image || null,
    has_sub_items: hasSubItems,
    material_id: hasSubItems ? null : data.material_id,
    ...(hasSubItems ? noRates : ownRates(data, hasSizes)),
    cut_length: hasSubItems ? null : ownCutLength(data, hasSizes),
    sizes: sizesPayload(data.sizes, !hasSubItems, !hasSubItems),
    sub_items: hasSubItems
      ? data.sub_items.map((row) => ({
          id: row.sub_item_id,
          name: row.code?.trim() || null,
          description: row.description,
          image: row.image || null,
          pieces: Number(row.pieces),
          material_id: row.material_id,
          ...ownRates(row, hasSizes),
          cut_length: ownCutLength(row, hasSizes),
          sizes: sizesPayload(inheritedSizeRows(data.sizes, row.sizes)),
        }))
      : [],
  };
};

// Next free code for a category, e.g. AB-03.
const fetchNextCode = async (categoryId: number) => {
  let code = "";
  await GET("/v1/admin/items/next-code", { category_id: categoryId })
    .then((response: any) => {
      if (response?.data?.status === "success") {
        code = response.data.data.code;
      }
    })
    .catch(() => {});
  return code;
};

type Props = {
  defaultValues?: IFormInput;
  savedName?: string;
  onSubmit: (data: IFormInput) => void;
  isSubmitting: boolean;
  onCancel: () => void;
  isEdit?: boolean;
};

const ItemForm = ({
  defaultValues,
  savedName = "",
  onSubmit,
  isSubmitting,
  onCancel,
  isEdit,
}: Props) => {
  const categories = useAppSelector(getItemCategoryOptions);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<IFormInput>({
    defaultValues: defaultValues || emptyItemForm(),
  });

  const name = useWatch({ control, name: "name" });
  const hasSubItems = useWatch({ control, name: "has_sub_items" });

  // Saved sub items that saving without the tick will remove.
  const savedSubItemCount =
    defaultValues?.sub_items.filter((row) => row.sub_item_id).length || 0;

  const categoryOptions = categories.map((category) => ({
    value: category.id,
    label: `${category.name} (${category.code})`,
  }));

  const handleChangeCategory = async (categoryId: number | null) => {
    const previousCode = categories.find(
      (category) => category.id === getValues("category_id")
    )?.code;
    setValue("category_id", categoryId, { shouldValidate: true });

    // Only replace a name that is empty or still the previous category's generated code.
    const currentName = getValues("name").trim();
    const isGenerated =
      !currentName ||
      (!!previousCode &&
        new RegExp(`^${previousCode}-\\d+$`).test(currentName));

    if (!categoryId || !isGenerated) return;

    // Back on its saved category, the item keeps its saved code instead of taking a new number.
    const code =
      isEdit && savedName && defaultValues?.category_id === categoryId
        ? savedName
        : await fetchNextCode(categoryId);

    if (code && getValues("category_id") === categoryId) {
      setValue("name", code, { shouldValidate: true });
    }
  };

  const handleOnSubmit: SubmitHandler<IFormInput> = (data) => {
    // A clashing sub item code is shown on its row; the server would refuse it too.
    const codes = subItemCodes(data.name, savedName, data.sub_items);

    if (
      data.has_sub_items &&
      subItemCodeProblems(data.name, codes).some(Boolean)
    ) {
      toast.error("સબ આઈટમ ના કોડ સુધારો", { ...(toastStyles as any) });
      return;
    }

    onSubmit(data);
  };

  return (
    <div className={formCardClass}>
      <div className={cx(cardContentClass, "pt-6")}>
        <form onSubmit={handleSubmit(handleOnSubmit)} className="space-y-6">
          <div className="space-y-2">
            <label className="flex cursor-pointer select-none items-start gap-2 rounded-lg border-2 border-gray-200 px-4 py-3 hover:border-[#34699A]">
              <input
                type="checkbox"
                className={cx(checkboxClass, "mt-0.5")}
                {...register("has_sub_items")}
              />
              <span className="min-w-0">
                <span className={cx(fieldLabelClass, "block")}>
                  સબ આઈટમ છે?
                </span>
                <span className="block text-xs text-gray-500">
                  આઈટમ બીજા ભાગ (સબ આઈટમ્સ) થી બનતી હોય તો ટિક કરો, દરેક ભાગનું
                  મટીરીયલ, કટીંગ લંબાઈ અને ભાવ અલગ નક્કી થશે
                </span>
              </span>
            </label>
            {!hasSubItems && savedSubItemCount > 0 && (
              <p className="flex items-center text-sm text-orange-700">
                <LuAlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                સેવ કરતાં {savedSubItemCount} સબ આઈટમ કાઢી નાખવામાં આવશે
              </p>
            )}
          </div>

          {/* An item without sub items picks its own material, beside its code. */}
          <div
            className={cx(
              "grid grid-cols-1 gap-4",
              hasSubItems ? "md:grid-cols-2" : "md:grid-cols-3"
            )}
          >
            <div className="space-y-2">
              <label className={fieldLabelClass}>
                કેટેગરી <span className="text-red-500">*</span>
              </label>
              <div className={cx("relative", errors.category_id && "error")}>
                <LuFolder className={formSelectIconClass} />
                <Controller
                  control={control}
                  name="category_id"
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select
                      inputId="item-category"
                      value={
                        categoryOptions.find(
                          (option) => option.value === field.value
                        ) || null
                      }
                      options={categoryOptions}
                      placeholder="કેટેગરી પસંદ કરો"
                      noOptionsMessage={() => "કેટેગરી મળી નથી"}
                      styles={iconSelectStyles}
                      isSearchable
                      className={cx(
                        "react-select-container",
                        errors.category_id && "error"
                      )}
                      classNamePrefix="react-select"
                      onChange={(option: any) =>
                        handleChangeCategory(option?.value ?? null)
                      }
                    />
                  )}
                />
              </div>
              {errors.category_id && (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  કેટેગરી પસંદ કરો
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className={fieldLabelClass}>
                નામ (કોડ) <span className="text-red-500">*</span>
              </label>
              <div className={cx("relative", errors.name && "error")}>
                <TagIcon className={formFieldIconClass} />
                <Input
                  type="text"
                  maxLength={255}
                  placeholder="દા.ત. AB-01"
                  className={fieldInputClass(!!errors.name)}
                  {...register("name", {
                    validate: (value) => value.trim().length > 0,
                  })}
                />
                {errors.name && <LuAlertCircle className={fieldErrorIconClass} />}
              </div>
              {errors.name ? (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  આઈટમનુ નામ (કોડ) દાખલ કરો
                </p>
              ) : (
                <p className="text-xs text-gray-500">
                  કેટેગરી પસંદ કરતાં તેના કોડ પરથી આગળનો કોડ આવી જશે, જરૂર હોય તો
                  બદલી શકાય
                </p>
              )}
            </div>

            {!hasSubItems && (
              <ItemMaterialField
                control={control}
                name="material_id"
                error={errors.material_id}
                idPrefix="item"
              />
            )}
          </div>

          {/* Below the material, and in the same place without it. An item with sub items only picks the sizes
              here: each sub item gets them, with its own cut lengths and rates. */}
          <ItemSizeRates
            control={control}
            getValues={getValues}
            setValue={setValue}
            prefix=""
            idPrefix="item"
            withCutLength={!hasSubItems}
            withRates={!hasSubItems}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* The description grows to the height of the photo beside it. */}
            <div className="flex flex-col space-y-2">
              <label className={fieldLabelClass}>ડિસ્ક્રિપ્શન (વૈકલ્પિક)</label>
              <div className="relative flex flex-1 flex-col">
                <FileTextIcon className={formFieldIconClass} />
                <Textarea
                  placeholder="આઈટમનુ ડિસ્ક્રિપ્શન દાખલ કરો"
                  className={cx(fieldTextareaClass(), "flex-1")}
                  {...register("description", { required: false })}
                />
              </div>
            </div>

            {/* Laid out like the description, so the two boxes line up. */}
            <div className="flex flex-col space-y-2">
              <label className={fieldLabelClass}>ફોટો (વૈકલ્પિક)</label>
              <Controller
                control={control}
                name="image"
                render={({ field }) => (
                  <ItemPhoto
                    value={field.value}
                    existingUrl={defaultValues?.image_url}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
          </div>

          {hasSubItems && (
            <SubItemsForm
              control={control}
              register={register}
              getValues={getValues}
              setValue={setValue}
              errors={errors}
              name={name}
              savedName={savedName}
            />
          )}

          <div className="flex flex-col md:flex-row md:items-center md:justify-end gap-4 pt-4">
            <div className="flex justify-end space-x-4">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                રદ કરો
              </Button>

              <button
                type="submit"
                className={gradientSubmitButtonClass}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <div className={buttonSpinnerClass}></div>
                    સાચવી રહ્યા છીએ...
                  </div>
                ) : (
                  "સાચવો"
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
export default ItemForm;
